import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { workItemApi } from '../services/api';
import { useAppStore } from '../store/useAppStore';

// A path segment looks like a displayId: PO-3, EP-1, FE-1, ST-2, TS-12, XX-4.
const SEGMENT = /^[A-Za-z]{2,4}-\d+$/;

/**
 * Resolves a human-readable full-path link, e.g.
 *   /PO-3/EP-1/FE-1/TS-1
 * The chain is unique (each segment is unique within the one above it). We
 * resolve it to the item's stable _id via the API, then hand off to the
 * `?item=<_id>` query param which opens the detail drawer on the project board.
 */
export default function WorkItemPathResolver() {
  const location = useLocation();
  const navigate = useNavigate();
  const setDetailItemId = useAppStore((s) => s.setDetailItemId);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    if (segments.length < 2 || !segments.every((s) => SEGMENT.test(s))) {
      setNotFound(true);
      return;
    }
    let cancelled = false;
    workItemApi
      .getByPath(segments)
      .then((item) => {
        if (cancelled) return;
        if (!item?._id) {
          setNotFound(true);
          return;
        }
        setDetailItemId(item._id);
        const dest = item.projectId ? `/project/${item.projectId}/board` : '/board';
        navigate(`${dest}?item=${encodeURIComponent(item._id)}`, { replace: true });
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname, navigate, setDetailItemId]);

  return (
    <div className="p-8 text-sm text-gray-500 dark:text-gray-400">
      {notFound ? 'Work item not found for this link.' : 'Opening item…'}
    </div>
  );
}
